import React, { useState, useEffect } from 'react';
import { Card, Title, Metric, Text, Flex } from '@tremor/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { DashboardService, TriageMetrics } from '../services/dashboardService';
import { ReferralDetailModal } from '../components/ReferralDetailModal';
import { ApiService, Referral } from '../services/apiService';
import { DashboardConfigService, DashboardConfig, DashboardWidget } from '../services/dashboardConfigService';
import { DashboardBuilder } from '../components/DashboardBuilder';
import { useCurrentUser } from '../hooks/useCurrentUser';

type TimeWindow = '7days' | '30days' | 'custom';

// Axis brand colors
const AXIS_COLORS = {
  orange: '#E84B37',
  purple: '#200A24',
  blueGray: '#A1B7B7',
  charcoal: '#333E48',
};

const CATEGORY_COLORS = [AXIS_COLORS.orange, AXIS_COLORS.purple, AXIS_COLORS.blueGray, AXIS_COLORS.charcoal];

export const DashboardPage: React.FC = () => {
  const { user: currentUser } = useCurrentUser();
  const [metrics, setMetrics] = useState<TriageMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('7days');
  const [selectedReferral, setSelectedReferral] = useState<Referral | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [drillDownCategory, setDrillDownCategory] = useState<number | null>(null);
  const [drillDownReferrals, setDrillDownReferrals] = useState<Referral[]>([]);
  const [showDrillDown, setShowDrillDown] = useState(false);
  
  // Dashboard management state
  const [showBuilder, setShowBuilder] = useState(false);
  const [savingDashboard, setSavingDashboard] = useState(false);
  const [dashboardConfig, setDashboardConfig] = useState<DashboardConfig>({
    widgets: [],
    layout: 'grid',
    timeWindow: '7days',
  });

  useEffect(() => {
    loadDashboardConfig();
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [timeWindow]);

  // Load shared dashboard configuration
  const loadDashboardConfig = async () => {
    try {
      const sharedConfig = await DashboardConfigService.getDashboardConfig();
      const config = sharedConfig.dashboard_config as DashboardConfig;
      
      if (config.timeWindow) {
        setTimeWindow(config.timeWindow);
      }
      
      // Ensure widgets array exists
      const fullConfig: DashboardConfig = {
        widgets: config.widgets || [],
        layout: config.layout || 'grid',
        timeWindow: config.timeWindow || '7days',
        filters: config.filters || {},
      };
      setDashboardConfig(fullConfig);
    } catch (err) {
      console.error('Error loading dashboard config:', err);
      // Don't show error to user - just continue with default view
    }
  };

  // Generate current dashboard configuration
  const getCurrentDashboardConfig = (): DashboardConfig => {
    return {
      ...dashboardConfig,
      timeWindow: timeWindow,
    };
  };

  // Handle saving from builder
  const handleSaveFromBuilder = async () => {
    try {
      setSavingDashboard(true);
      const config = getCurrentDashboardConfig();
      await DashboardConfigService.updateDashboardConfig(config);
      await loadDashboardConfig(); // Reload config
      setShowBuilder(false);
    } catch (err) {
      throw err;
    } finally {
      setSavingDashboard(false);
    }
  };

  const getDateRange = (window: TimeWindow): { from: string; to: string } => {
    const to = new Date();
    const from = new Date();
    
    switch (window) {
      case '7days':
        from.setDate(to.getDate() - 7);
        break;
      case '30days':
        from.setDate(to.getDate() - 30);
        break;
      default:
        from.setDate(to.getDate() - 7);
    }
    
    return {
      from: from.toISOString(),
      to: to.toISOString()
    };
  };

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const dateRange = getDateRange(timeWindow);
      const data = await DashboardService.getTriageMetrics(dateRange.from, dateRange.to);
      setMetrics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch dashboard metrics');
      console.error('Error fetching dashboard metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = async (category: number) => {
    try {
      setDrillDownCategory(category);
      setLoading(true);
      // Fetch referrals for this category
      const data = await ApiService.getReferrals(
        1,
        100,
        'completed',
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        'received_at',
        'desc',
      );
      // Filter by category - we'll need to check triage_category_suggested
      const filtered = data.referrals.filter(r => 
        r.triage_category_suggested === category
      );
      setDrillDownReferrals(filtered);
      setShowDrillDown(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch referrals');
    } finally {
      setLoading(false);
    }
  };

  const handleReferralClick = async (referralId: string) => {
    try {
      const referral = await ApiService.getReferralById(referralId);
      setSelectedReferral(referral);
      setShowDetailModal(true);
      setShowDrillDown(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch referral details');
    }
  };

  const categoryChartData = metrics ? [
    { name: 'Category 1', value: metrics.category_distribution.category_1 },
    { name: 'Category 2', value: metrics.category_distribution.category_2 },
    { name: 'Category 3', value: metrics.category_distribution.category_3 },
    { name: 'Category 4', value: metrics.category_distribution.category_4 },
  ] : [];

  const editedVsUneditedData = metrics ? [
    { name: 'Edited', value: metrics.edited_vs_unedited.edited_count },
    { name: 'Unedited', value: metrics.edited_vs_unedited.unedited_count },
  ] : [];

  // Get metric value based on metric ID
  const getMetricValue = (metricId: string): { value: number; label: string } => {
    if (!metrics) return { value: 0, label: '' };

    switch (metricId) {
      case 'total_referrals':
        return { value: metrics.total_referrals, label: 'Completed referrals with triage data' };
      case 'escalation_count':
        return {
          value: metrics.escalation_count,
          label: metrics.total_referrals > 0
            ? `${((metrics.escalation_count / metrics.total_referrals) * 100).toFixed(1)}% of total`
            : '0% of total'
        };
      case 'edited_count':
        return {
          value: metrics.edited_vs_unedited.edited_count,
          label: `${metrics.edited_vs_unedited.edited_percentage}% of total`
        };
      case 'unedited_count':
        return {
          value: metrics.edited_vs_unedited.unedited_count,
          label: `${metrics.edited_vs_unedited.unedited_percentage}% of total`
        };
      case 'category_1':
        return {
          value: metrics.category_distribution.category_1,
          label: metrics.total_referrals > 0
            ? `${((metrics.category_distribution.category_1 / metrics.total_referrals) * 100).toFixed(1)}% of total`
            : '0% of total'
        };
      case 'category_2':
        return {
          value: metrics.category_distribution.category_2,
          label: metrics.total_referrals > 0
            ? `${((metrics.category_distribution.category_2 / metrics.total_referrals) * 100).toFixed(1)}% of total`
            : '0% of total'
        };
      case 'category_3':
        return {
          value: metrics.category_distribution.category_3,
          label: metrics.total_referrals > 0
            ? `${((metrics.category_distribution.category_3 / metrics.total_referrals) * 100).toFixed(1)}% of total`
            : '0% of total'
        };
      case 'category_4':
        return {
          value: metrics.category_distribution.category_4,
          label: metrics.total_referrals > 0
            ? `${((metrics.category_distribution.category_4 / metrics.total_referrals) * 100).toFixed(1)}% of total`
            : '0% of total'
        };
      default:
        return { value: 0, label: '' };
    }
  };

  // Get chart data based on data source
  const getChartData = (dataSource: string) => {
    if (!metrics) return [];

    if (dataSource === 'category_distribution') {
      return categoryChartData;
    } else if (dataSource === 'edited_vs_unedited') {
      return editedVsUneditedData;
    }
    return [];
  };

  // Render a widget based on its configuration
  const renderWidget = (widget: DashboardWidget) => {
    if (!metrics) return null;

    switch (widget.type) {
      case 'kpi':
        const metricValue = getMetricValue(widget.config.metric || 'total_referrals');
        return (
          <Card key={widget.id} decoration="top" decorationColor="slate" style={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <Text style={{ fontSize: '0.875rem', fontWeight: '500', color: '#6b7280' }}>{widget.title}</Text>
            <Metric style={{ fontSize: '2.5rem', marginTop: '0.5rem' }}>{metricValue.value}</Metric>
            <Text style={{ marginTop: '0.5rem', color: '#9ca3af' }}>{metricValue.label}</Text>
          </Card>
        );

      case 'bar':
        const barData = getChartData(widget.config.dataSource || 'category_distribution');
        return (
          <Card key={widget.id}>
            <Title>{widget.title}</Title>
            {barData.length > 0 ? (
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={barData}
                    style={{ cursor: 'pointer' }}
                    onClick={(data: any) => {
                      if (data && data.activePayload && data.activePayload.length > 0 && widget.config.dataSource === 'category_distribution') {
                        const categoryName = data.activePayload[0].payload.name;
                        const categoryMap: Record<string, number> = {
                          'Category 1': 1, 'Category 2': 2, 'Category 3': 3, 'Category 4': 4,
                        };
                        const category = categoryMap[categoryName];
                        if (category) {
                          handleCategoryClick(category);
                        }
                      }
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar 
                      dataKey="value" 
                      fill={AXIS_COLORS.orange}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Text>No data available</Text>
            )}
          </Card>
        );

      case 'donut':
        const donutData = getChartData(widget.config.dataSource || 'category_distribution');
        const colors = widget.config.dataSource === 'category_distribution' ? CATEGORY_COLORS : [AXIS_COLORS.orange, AXIS_COLORS.blueGray];
        return (
          <Card key={widget.id}>
            <Title>{widget.title}</Title>
            {donutData.length > 0 ? (
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart style={{ cursor: 'pointer' }}>
                    <Pie
                      data={donutData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => percent ? `${name}: ${(percent * 100).toFixed(0)}%` : name}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      onClick={(data: any) => {
                        if (data && data.name && widget.config.dataSource === 'category_distribution') {
                          const categoryMap: Record<string, number> = {
                            'Category 1': 1, 'Category 2': 2, 'Category 3': 3, 'Category 4': 4,
                          };
                          const category = categoryMap[data.name];
                          if (category) {
                            handleCategoryClick(category);
                          }
                        }
                      }}
                    >
                      {donutData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Text>No data available</Text>
            )}
          </Card>
        );

      case 'area':
        const areaData = getChartData(widget.config.dataSource || 'category_distribution');
        return (
          <Card key={widget.id}>
            <Title>{widget.title}</Title>
            {areaData.length > 0 ? (
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={areaData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="value" stroke={AXIS_COLORS.orange} fill={AXIS_COLORS.orange} fillOpacity={0.6} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Text>No data available</Text>
            )}
          </Card>
        );

      case 'line':
        const lineData = getChartData(widget.config.dataSource || 'category_distribution');
        return (
          <Card key={widget.id}>
            <Title>{widget.title}</Title>
            {lineData.length > 0 ? (
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={lineData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="value" stroke={AXIS_COLORS.orange} fill={AXIS_COLORS.orange} fillOpacity={0.1} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Text>No data available</Text>
            )}
          </Card>
        );

      default:
        return null;
    }
  };

  // Debug: Log data to console
  useEffect(() => {
    if (metrics) {
      console.log('Dashboard metrics:', metrics);
      console.log('Category chart data:', categoryChartData);
      console.log('Edited vs unedited data:', editedVsUneditedData);
      console.log('Chart data structure check:', {
        hasData: categoryChartData.length > 0,
        sampleItem: categoryChartData[0],
        allHaveValues: categoryChartData.every(item => typeof item.value === 'number')
      });
    }
  }, [metrics]);

  if (loading && !metrics) {
    return (
      <div className="dashboard-page" style={{ padding: '2rem' }}>
        <h1>Triage Dashboard</h1>
        <p>Loading dashboard metrics...</p>
      </div>
    );
  }

  if (error && !metrics) {
    return (
      <div className="dashboard-page" style={{ padding: '2rem' }}>
        <h1>Triage Dashboard</h1>
        <div className="error-message" style={{ padding: '1rem', background: '#fee', color: '#c33', borderRadius: '4px' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page" style={{ padding: '2rem', maxWidth: '1600px', margin: '0 auto', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '2.5rem', marginBottom: '0.5rem', fontWeight: 'bold', color: '#111827' }}>Triage Dashboard</h1>
            <p style={{ color: '#6b7280', fontSize: '1.1rem' }}>
              View aggregate triage metrics and drill into specific referrals
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '0.5rem', marginRight: '1rem' }}>
              <button
                onClick={() => setTimeWindow('7days')}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: timeWindow === '7days' ? '2px solid #3b82f6' : '1px solid #d1d5db',
                  background: timeWindow === '7days' ? '#3b82f6' : 'white',
                  color: timeWindow === '7days' ? 'white' : '#374151',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: timeWindow === '7days' ? '600' : '400',
                  transition: 'all 0.2s'
                }}
              >
                Last 7 Days
              </button>
              <button
                onClick={() => setTimeWindow('30days')}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: timeWindow === '30days' ? '2px solid #3b82f6' : '1px solid #d1d5db',
                  background: timeWindow === '30days' ? '#3b82f6' : 'white',
                  color: timeWindow === '30days' ? 'white' : '#374151',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: timeWindow === '30days' ? '600' : '400',
                  transition: 'all 0.2s'
                }}
              >
                Last 30 Days
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setShowBuilder(true)}
                className="btn btn-primary"
                style={{ fontSize: '0.9rem', padding: '0.6rem 1.2rem' }}
              >
                Edit Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ padding: '1rem', background: '#fee', color: '#c33', borderRadius: '4px', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {showDrillDown ? (
        <div>
          <button
            onClick={() => {
              setShowDrillDown(false);
              setDrillDownCategory(null);
              setDrillDownReferrals([]);
            }}
            style={{
              marginBottom: '1rem',
              padding: '0.5rem 1rem',
              background: '#f0f0f0',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ← Back to Dashboard
          </button>
          <h2>Category {drillDownCategory} Referrals</h2>
          <div style={{ marginTop: '1rem' }}>
            {drillDownReferrals.length === 0 ? (
              <p>No referrals found for this category</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #ccc' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Patient</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Completed Date</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {drillDownReferrals.map(referral => (
                    <tr key={referral.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '0.75rem' }}>
                        {referral.patient_first_name} {referral.patient_last_name}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {referral.updated_at ? new Date(referral.updated_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <button
                          onClick={() => handleReferralClick(referral.id)}
                          style={{
                            padding: '0.25rem 0.75rem',
                            background: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : (
        <>
          {metrics && (
            // Render widgets from config if available, otherwise show default widgets
            dashboardConfig.widgets && dashboardConfig.widgets.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                {dashboardConfig.layout === 'grid' ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
                    {dashboardConfig.widgets.map(widget => renderWidget(widget))}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {dashboardConfig.widgets.map(widget => renderWidget(widget))}
                  </div>
                )}
              </div>
            ) : (
              // Default widgets when no custom widgets are configured
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                  <Card decoration="top" decorationColor="slate" style={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
                    <Text style={{ fontSize: '0.875rem', fontWeight: '500', color: '#6b7280' }}>Total Referrals</Text>
                    <Metric style={{ fontSize: '2.5rem', marginTop: '0.5rem' }}>{metrics.total_referrals}</Metric>
                    <Text style={{ marginTop: '0.5rem', color: '#9ca3af' }}>
                      Completed referrals with triage data
                    </Text>
                  </Card>
                  <Card decoration="top" decorationColor="orange" style={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
                    <Text style={{ fontSize: '0.875rem', fontWeight: '500', color: '#6b7280' }}>Escalations</Text>
                    <Metric style={{ fontSize: '2.5rem', marginTop: '0.5rem' }}>{metrics.escalation_count}</Metric>
                    <Text style={{ marginTop: '0.5rem', color: '#9ca3af' }}>
                      {metrics.total_referrals > 0 
                        ? `${((metrics.escalation_count / metrics.total_referrals) * 100).toFixed(1)}% of total`
                        : '0% of total'}
                    </Text>
                  </Card>
                  <Card decoration="top" decorationColor="orange" style={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
                    <Text style={{ fontSize: '0.875rem', fontWeight: '500', color: '#6b7280' }}>Edited</Text>
                    <Metric style={{ fontSize: '2.5rem', marginTop: '0.5rem' }}>{metrics.edited_vs_unedited.edited_count}</Metric>
                    <Text style={{ marginTop: '0.5rem', color: '#9ca3af' }}>
                      {metrics.edited_vs_unedited.edited_percentage}% of total
                    </Text>
                  </Card>
                  <Card decoration="top" decorationColor="slate" style={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
                    <Text style={{ fontSize: '0.875rem', fontWeight: '500', color: '#6b7280' }}>Unedited</Text>
                    <Metric style={{ fontSize: '2.5rem', marginTop: '0.5rem' }}>{metrics.edited_vs_unedited.unedited_count}</Metric>
                    <Text style={{ marginTop: '0.5rem', color: '#9ca3af' }}>
                      {metrics.edited_vs_unedited.unedited_percentage}% of total
                    </Text>
                  </Card>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '2rem', marginBottom: '2rem' }}>
                  <Card>
                    <Title>Priority Distribution</Title>
                    <Text>Click on a bar to view referrals in that category</Text>
                    {categoryChartData.length > 0 ? (
                      <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={categoryChartData}
                            style={{ cursor: 'pointer' }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar 
                              dataKey="value" 
                              fill={AXIS_COLORS.orange}
                              onClick={(data: any) => {
                                if (data && data.name) {
                                  const categoryMap: Record<string, number> = {
                                    'Category 1': 1,
                                    'Category 2': 2,
                                    'Category 3': 3,
                                    'Category 4': 4,
                                  };
                                  const category = categoryMap[data.name];
                                  if (category) {
                                    handleCategoryClick(category);
                                  }
                                }
                              }}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <Text>No data available</Text>
                    )}
                  </Card>

                  <Card>
                    <Title>Category Distribution (Pie Chart)</Title>
                    <Text>Visual breakdown of triage categories</Text>
                    {categoryChartData.length > 0 ? (
                      <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart style={{ cursor: 'pointer' }}>
                            <Pie
                              data={categoryChartData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => percent ? `${name}: ${(percent * 100).toFixed(0)}%` : name}
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="value"
                              onClick={(data: any) => {
                                if (data && data.name) {
                                  const categoryMap: Record<string, number> = {
                                    'Category 1': 1,
                                    'Category 2': 2,
                                    'Category 3': 3,
                                    'Category 4': 4,
                                  };
                                  const category = categoryMap[data.name];
                                  if (category) {
                                    handleCategoryClick(category);
                                  }
                                }
                              }}
                            >
                              {categoryChartData.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <Text>No data available</Text>
                    )}
                  </Card>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '2rem', marginBottom: '2rem' }}>
                  <Card>
                    <Title>Edited vs Unedited Triage</Title>
                    <Text>Comparison of AI-suggested vs clinician-edited triage categories</Text>
                    {editedVsUneditedData.length > 0 ? (
                      <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={editedVsUneditedData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => percent ? `${name}: ${(percent * 100).toFixed(0)}%` : name}
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {editedVsUneditedData.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={index === 0 ? AXIS_COLORS.orange : AXIS_COLORS.blueGray} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <Text>No data available</Text>
                    )}
                    <Flex justifyContent="center" className="mt-4">
                      <Text>
                        <strong>Edited:</strong> {metrics.edited_vs_unedited.edited_count} ({metrics.edited_vs_unedited.edited_percentage}%) | 
                        <strong> Unedited:</strong> {metrics.edited_vs_unedited.unedited_count} ({metrics.edited_vs_unedited.unedited_percentage}%)
                      </Text>
                    </Flex>
                  </Card>

                  <Card>
                    <Title>Category Comparison</Title>
                    <Text>Side-by-side comparison of all categories</Text>
                    {categoryChartData.length > 0 ? (
                      <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={categoryChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Area type="monotone" dataKey="value" stroke={AXIS_COLORS.orange} fill={AXIS_COLORS.orange} fillOpacity={0.6} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <Text>No data available</Text>
                    )}
                  </Card>
                </div>

                {metrics.total_referrals > 0 && (
                  <Card style={{ marginBottom: '2rem' }}>
                    <Title>Category Breakdown</Title>
                    <Text>Detailed view of priority distribution</Text>
                    {categoryChartData.length > 0 ? (
                      <div className="h-[300px] w-full mt-4">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={categoryChartData}
                            layout="vertical"
                            style={{ cursor: 'pointer' }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="name" type="category" width={100} />
                            <Tooltip />
                            <Bar 
                              dataKey="value" 
                              fill={AXIS_COLORS.orange}
                              onClick={(data: any) => {
                                if (data && data.name) {
                                  const categoryMap: Record<string, number> = {
                                    'Category 1': 1,
                                    'Category 2': 2,
                                    'Category 3': 3,
                                    'Category 4': 4,
                                  };
                                  const category = categoryMap[data.name];
                                  if (category) {
                                    handleCategoryClick(category);
                                  }
                                }
                              }}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <Text>No data available</Text>
                    )}
                  </Card>
                )}

                <Card>
                  <Title>Quick Stats by Category</Title>
                  <Text>Click any card to drill down into referrals</Text>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
                    {[1, 2, 3, 4].map(category => {
                      const count = metrics.category_distribution[`category_${category}` as keyof typeof metrics.category_distribution] as number;
                      const percentage = metrics.total_referrals > 0 
                        ? ((count / metrics.total_referrals) * 100).toFixed(1) 
                        : '0';
                      const decorationColors = ['orange', 'slate', 'slate', 'slate'];
                      return (
                        <Card
                          key={category}
                          decoration="top"
                          decorationColor={decorationColors[category - 1] as any}
                          style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                          onClick={() => handleCategoryClick(category)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <Text>Category {category}</Text>
                          <Metric>{count}</Metric>
                          <Text>{percentage}% of total</Text>
                        </Card>
                      );
                    })}
                  </div>
                </Card>
              </>
            )
          )}
        </>
      )}

      {selectedReferral && (
        <ReferralDetailModal
          referral={selectedReferral}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedReferral(null);
          }}
          userRole={currentUser?.role}
        />
      )}

      {showBuilder && (
        <DashboardBuilder
          config={dashboardConfig}
          onConfigChange={setDashboardConfig}
          onClose={() => setShowBuilder(false)}
          onSave={handleSaveFromBuilder}
          isSaving={savingDashboard}
        />
      )}
    </div>
  );
};

